import { useState } from 'react';
import type { DamageCell, DamageMatrixPayload } from '../../server/routes/dashboards.ts';
import type { SpeciesId } from '../types.ts';
import { Sprite } from './Sprite.tsx';

// Damage matrix grid — the same layout used for "your team vs top 5 meta",
// "top 5 meta vs you", and the 2v2 lead-pair offence/defence panels. Each
// cell holds a `byMove` map; the client picks "best of all moves" (default)
// or a specific move per row when allowMoveToggle is true.
//
// Color + glyph tier per cell:
//   OHKO     → red tint   · `!`
//   ≥50%     → amber tint · `~`
//   resisted → green tint · `·`
// Non-color glyph survives grayscale and color-blind viewing — paired with
// the MatrixLegend component the section header renders above the table.

export function DamageMatrixGrid({
  matrix,
  direction,
  showMoveHeaders,
  allowMoveToggle,
}: {
  matrix: DamageMatrixPayload;
  direction: 'offence' | 'defence';
  showMoveHeaders?: boolean;
  allowMoveToggle?: boolean;
}) {
  const [selectedByRow, setSelectedByRow] = useState<Record<number, string | null>>({});

  if (matrix.attackers.length === 0 || matrix.defenders.length === 0) {
    return (
      <div
        className="mono"
        style={{ padding: '14px', color: 'var(--fg-4)', fontSize: 11.5, lineHeight: 1.5 }}
      >
        No data — Pikalytics ingestion for the top 5 meta is missing.
      </div>
    );
  }
  return (
    <div style={{ overflowX: 'auto', padding: '8px 4px' }}>
      <table
        className="mono"
        style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '4px', fontSize: 11 }}
      >
        <thead>
          <tr>
            <th style={{ width: 130, textAlign: 'left' }} />
            {matrix.defenders.map((d) => (
              <th
                key={d.species}
                style={{
                  textAlign: 'center',
                  padding: '4px 6px',
                  color: 'var(--fg-2)',
                  fontWeight: 500,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    justifyContent: 'center',
                  }}
                >
                  <Sprite species={d.species as SpeciesId} size={22} />
                  <span>{prettySpecies(d.label)}</span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.attackers.map((atk, ri) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: matrix rows are positional — order is part of the data contract, ri suffix disambiguates rare duplicate species
            <tr key={`${atk.species}-${ri}`}>
              <th
                style={{
                  textAlign: 'left',
                  padding: '4px 6px',
                  color: 'var(--fg-2)',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  position: 'sticky',
                  left: 0,
                  background: 'var(--bg-1)',
                  zIndex: 1,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Sprite species={atk.species as SpeciesId} size={22} />
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span>{prettySpecies(atk.label)}</span>
                    {showMoveHeaders && atk.move_header && (
                      <span style={{ color: 'var(--fg-4)', fontSize: 9.5 }}>
                        {atk.move_header}
                        {atk.move_usage_pct ? ` · ${atk.move_usage_pct.toFixed(0)}%` : ''}
                      </span>
                    )}
                    {allowMoveToggle && atk.moves && atk.moves.length > 0 && (
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 2,
                          marginTop: 3,
                          maxWidth: 220,
                        }}
                      >
                        {[null, ...atk.moves].map((mv) => {
                          const active = (selectedByRow[ri] ?? null) === mv;
                          const label = mv === null ? 'best' : mv;
                          return (
                            <button
                              key={mv ?? '__best__'}
                              type="button"
                              onClick={() => setSelectedByRow((prev) => ({ ...prev, [ri]: mv }))}
                              className="mono caps"
                              style={{
                                fontSize: 8,
                                padding: '1px 5px',
                                background: active ? 'var(--accent-bg)' : 'transparent',
                                color: active ? 'var(--accent-2)' : 'var(--fg-4)',
                                border: `1px solid ${
                                  active ? 'var(--accent-line)' : 'var(--line-1)'
                                }`,
                                borderRadius: 2,
                                letterSpacing: '0.05em',
                                cursor: 'pointer',
                              }}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </th>
              {matrix.cells[ri].map((cellEntry, ci) => {
                const cell = pickCell(cellEntry.byMove, selectedByRow[ri] ?? null);
                const unavailable = cellEntry.unavailable_reason;
                return (
                  <td
                    // Defender species is unique within a row (top-5 distinct).
                    key={matrix.defenders[ci].species}
                    style={{
                      padding: '6px 6px',
                      borderRadius: 3,
                      background: cellBg(cell),
                      border: `1px solid ${cellBorder(cell)}`,
                      color: 'var(--fg-1)',
                      textAlign: 'center',
                      minWidth: 84,
                    }}
                    title={cellTooltip(cell, atk.label, matrix.defenders[ci].label, direction)}
                  >
                    {cell ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <span
                          className="mono tabular"
                          style={{ fontSize: 11, fontWeight: 600, color: cellTextColor(cell) }}
                        >
                          <span aria-hidden="true" style={{ marginRight: 2, opacity: 0.8 }}>
                            {cellGlyph(cell)}
                          </span>
                          {cell.min_pct.toFixed(0)}–{cell.max_pct.toFixed(0)}%
                        </span>
                        <span
                          className="mono caps"
                          style={{ fontSize: 8.5, color: 'var(--fg-3)', letterSpacing: '0.04em' }}
                        >
                          {cell.ko_text}
                        </span>
                        {!showMoveHeaders && (
                          <span
                            className="mono"
                            style={{
                              fontSize: 9,
                              color: 'var(--fg-4)',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: 80,
                            }}
                          >
                            {cell.move}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <span style={{ color: 'var(--fg-4)', fontSize: 14 }}>—</span>
                        <span
                          className="mono caps"
                          style={{ fontSize: 8.5, color: 'var(--fg-4)', letterSpacing: '0.04em' }}
                        >
                          {unavailable === 'champions_mega_item'
                            ? 'mega n/a'
                            : unavailable === 'no_moves'
                              ? 'no moves'
                              : unavailable === 'no_meta_data'
                                ? 'no meta data'
                                : 'no calc'}
                        </span>
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// MatrixLegend renders the three glyph/color buckets above a matrix. Kept in
// this file so callers only import one module.
export function MatrixLegend() {
  const items: Array<{ glyph: string; bg: string; label: string }> = [
    { glyph: '!', bg: 'rgba(201,119,102,0.18)', label: 'OHKO' },
    { glyph: '~', bg: 'rgba(212,166,86,0.14)', label: '≥50% (2HKO-ish)' },
    { glyph: '·', bg: 'rgba(150,180,140,0.10)', label: 'resisted' },
  ];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {items.map((it) => (
        <span
          key={it.glyph}
          className="mono"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 9.5,
            color: 'var(--fg-3)',
            padding: '2px 6px',
            background: it.bg,
            border: '1px solid var(--line-1)',
            borderRadius: 2,
            letterSpacing: '0.04em',
          }}
        >
          <span style={{ fontWeight: 600 }}>{it.glyph}</span>
          {it.label}
        </span>
      ))}
    </div>
  );
}

// ─── internal helpers (private to this file) ─────────────────────────

function pickCell(byMove: Record<string, DamageCell>, selected: string | null): DamageCell {
  if (selected) return byMove[selected] ?? null;
  let best: DamageCell = null;
  for (const c of Object.values(byMove)) {
    if (c && (!best || c.max_pct > best.max_pct)) best = c;
  }
  return best;
}

function cellBg(cell: DamageCell): string {
  if (!cell) return 'var(--bg-2)';
  if (cell.is_ohko) return 'rgba(201,119,102,0.18)';
  if (cell.max_pct >= 50) return 'rgba(212,166,86,0.14)';
  if (cell.variant === 'resisted' || cell.variant === 'immune') return 'rgba(150,180,140,0.10)';
  return 'var(--bg-1)';
}
function cellBorder(cell: DamageCell): string {
  if (!cell) return 'var(--line-1)';
  if (cell.is_ohko) return 'rgba(201,119,102,0.45)';
  if (cell.max_pct >= 50) return 'rgba(212,166,86,0.35)';
  return 'var(--line-1)';
}
function cellTextColor(cell: NonNullable<DamageCell>): string {
  if (cell.is_ohko) return 'var(--neg)';
  if (cell.max_pct >= 50) return 'var(--warn)';
  return 'var(--fg-1)';
}
function cellGlyph(cell: NonNullable<DamageCell>): string {
  if (cell.is_ohko) return '!';
  if (cell.max_pct >= 50) return '~';
  if (cell.variant === 'resisted' || cell.variant === 'immune') return '·';
  return '';
}
// Directional tooltip — disambiguates which side is hitting which. Matches
// the section title's offence/defence framing.
function cellTooltip(
  cell: DamageCell,
  attackerLabel: string,
  defenderLabel: string,
  direction: 'offence' | 'defence',
): string {
  if (!cell) return 'no data';
  const subject = direction === 'offence' ? 'your' : 'their';
  const target = direction === 'offence' ? 'their' : 'your';
  return `${subject} ${attackerLabel} → ${target} ${defenderLabel} · ${cell.move} · ${cell.ko_text}`;
}

function prettySpecies(id: string): string {
  return id
    .split('-')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
}
